import React, { useEffect, useMemo, useState } from "react";
import { MoreVertical, ChevronsLeft, ChevronLeft, ChevronsRight, ChevronRight } from "lucide-react";
import { Button } from "../../@/components/ui/button";
import { Badge } from "../../@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../@/components/ui/dropdown-menu";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../@/components/ui/select";
import {
    Table,
    TableHeader,
    TableBody,
    TableHead,
    TableRow,
    TableCell,
} from "../../@/components/ui/table";
import DefaultTable from "./defaultTable";
import { Spinner } from "./spinner";
import Pagination from "./pagination";
import BatchUpdateCohortsType from "../forms/batchUpdateCohortsType";
import BatchDeleteCohorts from "../forms/batchDeleteCohorts";


export default function CohortDataTable({
    data = [],
    onEdit,
    onDelete,
    onEnrollStudents,
    onCohortsUpdated,
    loading = false
}) {
    const [currentPage, setCurrentPage] = useState(1)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [selectedIds, setSelectedIds] = useState(() => new Set())

    const [bulkEditTypeOpen, setBulkEditTypeOpen] = useState(false);
    const [bulkCohortsForTypeEdit, setBulkCohortsForTypeEdit] = useState([]);

    const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
    const [bulkCohortsForDelete, setBulkCohortsForDelete] = useState([]);

    const totalRows = data?.length || 0;
    const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
    const safeCurrentPage = Math.max(1, Math.min(currentPage, totalPages));
    const startIndex = (safeCurrentPage - 1) * rowsPerPage;
    const paginatedData = (data || []).slice(startIndex, startIndex + rowsPerPage);

    useEffect(() => {
        setCurrentPage(1);
    }, [rowsPerPage, totalRows]);

    useEffect(() => {
        if (safeCurrentPage !== currentPage) {
            setCurrentPage(safeCurrentPage);
        }
    }, [safeCurrentPage, currentPage]);

    const getRowId = (row) => row.id ?? row._id ?? JSON.stringify(row);
    const paginatedDataWithIds = paginatedData.map((row) => ({
        ...row,
        id: getRowId(row),
    }));

    const headerClass =
        "font-semibold font-heading text-(--neutral-700)";

    const formatDate = (value) => {
        if (!value) return "Not available";
        const date = new Date(value);
        if (Number.isNaN(date.valueOf())) return "Not available";
        return date.toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    const formatTypeLabel = (value) => {
        if (value == null || String(value).trim() === "") return "Not available";
        const upper = String(value).trim().toUpperCase();
        return upper
            .replace(/[_-]+/g, " ")
            .toLowerCase()
            .replace(/^./, (c) => c.toUpperCase());
    };

    const columns = useMemo(
        () => [
            { key: "checkbox" },
            {
                key: "name",
                header: <span className={headerClass}>Name</span>,
                render: (row) => (
                    <Badge variant="outline" className="font-body">
                        {row.name ?? "Not available"}
                    </Badge>
                ),
            },
            {
                key: "type",
                header: <span className={headerClass}>Type</span>,
                render: (row) => (
                    <Badge variant="default" className="font-body">
                        {formatTypeLabel(row.type)}
                    </Badge>
                ),
            },
            {
                key: "start",
                header: <span className={headerClass}>Start Date</span>,
                render: (row) => formatDate(row.start_date ?? row.start),
            },
            {
                key: "end",
                header: <span className={headerClass}>End Date</span>,
                render: (row) => formatDate(row.end_date ?? row.end),
            },
            {
                key: "action",
                header: "",
                render: (row) => (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="text-(--neutral-900)">
                                <MoreVertical className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            align="end"
                            className=""
                        >
                            {onEdit && (
                                <DropdownMenuItem onClick={() => onEdit(row)}>
                                    Edit Cohort
                                </DropdownMenuItem>
                            )}
                            {onDelete && (
                                <DropdownMenuItem
                                    onClick={() => onDelete(row)}
                                    className="text-(--color-destructive)"
                                >
                                    Delete Cohort
                                </DropdownMenuItem>
                            )}
                            {onEnrollStudents && (
                                <DropdownMenuItem onClick={() => onEnrollStudents(row)}>
                                    Enroll Students
                                </DropdownMenuItem>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                ),
            },
        ],
        [onEdit, onDelete, onEnrollStudents]
    );

    const toggleSelectRow = (id) =>
        setSelectedIds((prev) => {
            const copy = new Set(prev);
            copy.has(id) ? copy.delete(id) : copy.add(id);
            return copy;
        });

    const toggleSelectAll = (checked, pageRowKeys = []) =>
        setSelectedIds((prev) => {
            const copy = new Set(prev);

            if (checked) {
                (pageRowKeys || []).forEach((k) => copy.add(k));
                return copy;
            }

            (pageRowKeys || []).forEach((k) => copy.delete(k));
            return copy;
        });

    // const handleFirst = () => setCurrentPage(1);
    // const handleLast = () => setCurrentPage(totalPages);
    // const handlePrev = () => setCurrentPage((p) => Math.max(p - 1, 1));
    // const handleNext = () => setCurrentPage((p) => Math.min(p + 1, totalPages));

    return (
        <div className="">
            <DefaultTable
                columns={columns}
                data={paginatedDataWithIds}
                selectedItems={[...selectedIds]}
                onSelectItem={toggleSelectRow}
                onSelectAll={toggleSelectAll}
                bulkActions={[
                    ...(onEdit ? ["Edit Cohort"] : []),
                    ...(onDelete ? ["Delete Cohort"] : []),
                    ...(onEnrollStudents ? ["Enroll Students"] : []),
                ]}
                onBulkAction={(label, ctx) => {
                    const normalized = String(label || "").trim().toLowerCase();
                    const selectedRows = Array.isArray(ctx?.selectedRowsOnPage)
                        ? ctx.selectedRowsOnPage
                        : [];

                    if (normalized === "edit cohort") {
                        if (selectedRows.length < 2) return;

                        setBulkCohortsForTypeEdit(selectedRows);
                        setBulkEditTypeOpen(true);
                        return;
                    }

                    if (normalized === "delete cohort") {
                        if (selectedRows.length < 2) return;

                        setBulkCohortsForDelete(selectedRows);
                        setBulkDeleteOpen(true);
                        return;
                    }
                }}
                emptyMessage={loading ? <Spinner /> : "No items found."}
            />

            <BatchUpdateCohortsType
                open={bulkEditTypeOpen}
                onOpenChange={(next) => {
                    setBulkEditTypeOpen(next);
                    if (!next) setBulkCohortsForTypeEdit([]);
                }}
                selectedCohorts={bulkCohortsForTypeEdit}
                refetchCohorts={onCohortsUpdated}
                onUpdated={() => {
                    setSelectedIds(new Set());
                }}
            />

            <BatchDeleteCohorts
                open={bulkDeleteOpen}
                onOpenChange={(next) => {
                    setBulkDeleteOpen(next);
                    if (!next) setBulkCohortsForDelete([]);
                }}
                selectedCohorts={bulkCohortsForDelete}
                refetchCohorts={onCohortsUpdated}
                onDeleted={() => {
                    setSelectedIds(new Set());
                }}
            />

            {/* Pagination */}
            <Pagination
                currentPage={safeCurrentPage}
                totalPages={totalPages}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[10, 25, 50]}
                onRowsPerPageChange={(value) => {
                    setRowsPerPage(value);
                    setCurrentPage(1);
                }}
                onPageChange={(page) =>
                    setCurrentPage(Math.max(1, Math.min(page, totalPages)))
                }
                totalRows={totalRows}
                selectedCount={selectedIds.size}
                rowLabel="row items"
            />
        </div>
    );
}
